import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { resolveQueryColumnNames } from '@/tableDefinitionEditor/fetch/materializedTableFetch.utilities';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { Filter, FilterValue } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { FilterSpecV1 } from '@/tableDefinitionEditor/filterSpec.types';
import type { ColumnDefinition, TableJoinData } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FilterModel, GridApi } from '@seeqdev/ag/grid';

// --- Restoring a persisted filterSpec back into the AG Grid filter UI -------------------------------------------------
//
// This module deliberately avoids importing `materializedTables.utilities` (the save-side dependency), keeping it free
// of the heavy transitive `highcharts` import so it can be reached from the lightweight store graph (e.g. via
// `assetBuilder.utilities` → `assetBuilder.store`) without violating the `lightweight-stores` dependency rule.

/** A single AG Grid leaf filter model (text/number floating filter, i.e. the "search box"). */
type AgGridLeafFilterModel = {
  filterType: 'text' | 'number';
  type: string;
  filter?: FilterValue;
  filterTo?: FilterValue;
};

type AgGridSetFilterModel = { filterType: 'set'; values: FilterValue[] };

/** Backend filterType -> AG Grid text-filter option (the floating "search box" under text/uuid headers). */
const GRAPHQL_TO_TEXT_TYPE: Record<string, string> = {
  [GraphQL.FilterTypes.Contains]: 'contains',
  [GraphQL.FilterTypes.NotContains]: 'notContains',
  [GraphQL.FilterTypes.BeginsWith]: 'startsWith',
  [GraphQL.FilterTypes.EndsWith]: 'endsWith',
  [GraphQL.FilterTypes.Equals]: 'equals',
  [GraphQL.FilterTypes.NotEquals]: 'notEqual',
};

/** Backend filterType -> AG Grid number-filter option. */
const GRAPHQL_TO_NUMBER_TYPE: Record<string, string> = {
  [GraphQL.FilterTypes.Equals]: 'equals',
  [GraphQL.FilterTypes.NotEquals]: 'notEqual',
  [GraphQL.FilterTypes.GreaterThan]: 'greaterThan',
  [GraphQL.FilterTypes.GreaterThanOrEqual]: 'greaterThanOrEqual',
  [GraphQL.FilterTypes.LessThan]: 'lessThan',
  [GraphQL.FilterTypes.LessThanOrEqual]: 'lessThanOrEqual',
};

type FilterLeaf = { columnName: string; filterType: string; value?: FilterValue | FilterValue[]; isSimple: boolean };

/** Flattens a (possibly composite AND/OR) filter tree into its leaf value/simple filters. */
const collectFilterLeaves = (filter: Filter): FilterLeaf[] => {
  if ('compositeFilter' in filter) {
    return [
      ...collectFilterLeaves(filter.compositeFilter.filter1),
      ...collectFilterLeaves(filter.compositeFilter.filter2),
    ];
  }
  if ('valueFilter' in filter) {
    const { columnName, filterType, value } = filter.valueFilter;
    // valueFilter values are occasionally wrapped as `{ value }`; unwrap to the raw scalar/array.
    const unwrapped =
      value !== null && typeof value === 'object' && !Array.isArray(value)
        ? (value as { value: FilterValue | FilterValue[] }).value
        : value;
    return [{ columnName, filterType, value: unwrapped, isSimple: false }];
  }
  return [{ columnName: filter.simpleFilter.columnName, filterType: filter.simpleFilter.filterType, isSimple: true }];
};

/** Reduces a possibly-array filter value to the first scalar (single-condition filters carry one value). */
const firstScalar = (value: FilterValue | FilterValue[] | undefined): FilterValue | undefined =>
  Array.isArray(value) ? value[0] : value;

const buildTextLeafModel = (leaf: FilterLeaf): AgGridLeafFilterModel | undefined => {
  if (leaf.isSimple) {
    if (leaf.filterType === GraphQL.FilterTypes.IsNull) {
      return { filterType: 'text', type: 'blank' };
    }
    if (leaf.filterType === GraphQL.FilterTypes.IsNotNull) {
      return { filterType: 'text', type: 'notBlank' };
    }
    return undefined;
  }
  const type = GRAPHQL_TO_TEXT_TYPE[leaf.filterType];
  return type === undefined ? undefined : { filterType: 'text', type, filter: firstScalar(leaf.value) };
};

const buildNumberColumnModel = (leaves: FilterLeaf[]): AgGridLeafFilterModel | undefined => {
  const gte = leaves.find((leaf) => leaf.filterType === GraphQL.FilterTypes.GreaterThanOrEqual);
  const lte = leaves.find((leaf) => leaf.filterType === GraphQL.FilterTypes.LessThanOrEqual);
  if (gte && lte) {
    return { filterType: 'number', type: 'inRange', filter: firstScalar(gte.value), filterTo: firstScalar(lte.value) };
  }
  const leaf = leaves[0];
  if (leaf.isSimple) {
    if (leaf.filterType === GraphQL.FilterTypes.IsNull) {
      return { filterType: 'number', type: 'blank' };
    }
    if (leaf.filterType === GraphQL.FilterTypes.IsNotNull) {
      return { filterType: 'number', type: 'notBlank' };
    }
    return undefined;
  }
  const type = GRAPHQL_TO_NUMBER_TYPE[leaf.filterType];
  return type === undefined ? undefined : { filterType: 'number', type, filter: firstScalar(leaf.value) };
};

/** Collects the distinct set-filter selections (EQUALS / IN) for a column, if any. */
const buildSetColumnModel = (leaves: FilterLeaf[]): AgGridSetFilterModel | undefined => {
  const values: FilterValue[] = [];
  for (const leaf of leaves) {
    if (leaf.isSimple) {
      continue;
    }
    if (leaf.filterType === GraphQL.FilterTypes.Equals && leaf.value !== undefined && !Array.isArray(leaf.value)) {
      values.push(leaf.value);
    } else if (leaf.filterType === GraphQL.FilterTypes.In && Array.isArray(leaf.value)) {
      values.push(...leaf.value);
    }
  }
  return values.length > 0 ? { filterType: 'set', values } : undefined;
};

/**
 * Rebuilds the AG Grid `FilterModel` for one grid column from its persisted leaves. Text/UUID columns use the
 * `agMultiColumnFilter` shape `[textChild, setChild]`; a CONTAINS (or other text op) repopulates the floating
 * "search box" text child, while EQUALS/IN selections repopulate the set child. Numeric columns use a number
 * filter, and boolean columns a set filter.
 */
const buildColumnFilterModel = (column: ColumnDefinition, leaves: FilterLeaf[]): unknown => {
  if (column.columnType === ColumnTypeEnum.NUMERIC) {
    return buildNumberColumnModel(leaves);
  }
  if (column.columnType === ColumnTypeEnum.BOOLEAN) {
    return buildSetColumnModel(leaves);
  }

  // TEXT / UUID columns are configured as an agMultiColumnFilter (text child first, set child second).
  const setModel = buildSetColumnModel(leaves);
  const textLeaf = leaves.find(
    (leaf) =>
      !setModel || (leaf.filterType !== GraphQL.FilterTypes.Equals && leaf.filterType !== GraphQL.FilterTypes.In),
  );
  const textModel = textLeaf ? buildTextLeafModel(textLeaf) : undefined;
  if (!textModel && !setModel) {
    return undefined;
  }
  return { filterType: 'multi', filterModels: [textModel ?? null, setModel ?? null] };
};

/**
 * Reverse of `filterModelToTableFilter`: converts a persisted table {@link Filter} back into an AG Grid
 * `FilterModel` so the saved filter can be reapplied to the grid display (and its floating "search box"
 * repopulated for contains-style text filters).
 *
 * @exportedfortesting
 */
export const tableFilterToFilterModel = (
  filter: Filter | null | undefined,
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
): FilterModel => {
  if (!filter) {
    return {};
  }

  // Backend column name -> grid column (colId is column.id). Resolve every queryable name a column maps to.
  const columnByBackendName = new Map<string, ColumnDefinition>();
  columns.forEach((column) => {
    resolveQueryColumnNames(column, tableJoinData).forEach((name) => columnByBackendName.set(name, column));
  });

  const leavesByColumnId = new Map<string, { column: ColumnDefinition; leaves: FilterLeaf[] }>();
  for (const leaf of collectFilterLeaves(filter)) {
    const column = columnByBackendName.get(leaf.columnName);
    if (!column) {
      continue;
    }
    const existing = leavesByColumnId.get(column.id);
    if (existing) {
      existing.leaves.push(leaf);
    } else {
      leavesByColumnId.set(column.id, { column, leaves: [leaf] });
    }
  }

  const filterModel: FilterModel = {};
  for (const [colId, { column, leaves }] of leavesByColumnId) {
    const model = buildColumnFilterModel(column, leaves);
    if (model !== undefined) {
      filterModel[colId] = model;
    }
  }
  return filterModel;
};

/**
 * Reapplies a persisted `filterSpec` to the AG Grid display. The floating "search box" text filters are
 * repopulated from contains-style leaves; all other saved filters are restored to their column filter UI.
 * Uses `setFilterModel`, whose resulting `filterChanged` event has `source === 'api'` and is therefore ignored
 * by the user-driven persist path.
 */
export const applySavedFilterToGrid = (
  api: GridApi,
  filterSpec: FilterSpecV1 | undefined,
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
) => {
  const filterModel = tableFilterToFilterModel(filterSpec?.filter as Filter | undefined, columns, tableJoinData);
  api.setFilterModel(Object.keys(filterModel).length > 0 ? filterModel : null);
};
